import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Camera } from "lucide-react";
import { uploadAvatar, resolveMediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const EditProfileModal = ({ open, onOpenChange }) => {
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    bio: user?.bio || "",
    role: user?.role || "backer",
  });
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_url || "");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadAvatar(file);
      setAvatarPreview(result.avatar_url);
      toast.success("Avatar uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const result = await updateProfile(form);
    setBusy(false);
    if (result.ok) {
      toast.success("Profile updated");
      onOpenChange(false);
    } else {
      toast.error(result.error || "Update failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] rounded-2xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto" data-testid="edit-profile-modal">
        <div className="p-8">
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-2xl tracking-tight">Edit profile</DialogTitle>
            <DialogDescription className="text-navy-500">Update how creators and backers see you on Dwinov.</DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="mt-6 space-y-5" data-testid="edit-profile-form">
            <div className="flex items-center gap-4">
              <div className="relative h-20 w-20 rounded-2xl overflow-hidden bg-slate-100 border border-warm-200 grid place-items-center">
                {avatarPreview ? (
                  <img src={resolveMediaUrl(avatarPreview)} alt="Avatar" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-2xl font-semibold text-slate-400">{(user?.name || "U").charAt(0).toUpperCase()}</span>
                )}
                {uploading && <div className="absolute inset-0 bg-black/40 grid place-items-center"><Loader2 className="h-5 w-5 text-white animate-spin" /></div>}
              </div>
              <div>
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-xl" data-testid="upload-avatar-btn">
                  <Camera className="h-4 w-4 mr-2" /> Change photo
                </Button>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFileChange} className="hidden" data-testid="avatar-input" />
                <p className="mt-1 text-xs text-navy-500">PNG, JPG, or WEBP · max 5MB</p>
              </div>
            </div>

            <div>
              <Label htmlFor="ep-name">Name</Label>
              <Input id="ep-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 rounded-xl border-warm-200" data-testid="ep-name-input" />
            </div>
            <div>
              <Label htmlFor="ep-bio">Bio</Label>
              <Textarea id="ep-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={4} className="mt-1.5 rounded-xl border-warm-200" data-testid="ep-bio-input" />
            </div>
            <div>
              <Label>Role</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {[{ v: "backer", l: "Backer" }, { v: "creator", l: "Creator" }].map((r) => (
                  <button
                    type="button" key={r.v}
                    onClick={() => setForm({ ...form, role: r.v })}
                    data-testid={`ep-role-${r.v}`}
                    className={`px-3 py-2.5 rounded-xl text-sm font-medium border transition-colors ${form.role === r.v ? "border-saffron-500 bg-saffron-50 text-saffron-700" : "border-warm-200 text-navy-700 hover:border-saffron-200"}`}
                  >
                    {r.l}
                  </button>
                ))}
              </div>
            </div>
            <Button type="submit" disabled={busy} data-testid="ep-submit-btn" className="w-full h-11 rounded-2xl bg-saffron-600 hover:bg-saffron-700 hover:scale-[1.02] active:scale-95 transition-all">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditProfileModal;
